import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';

import { TableGrid } from './TableGrid';
import { CategoryPanel } from '../CategoryPanel';
import { Each } from 'GeminiViewerComponent/helpers/Each';
import { getAllCategories } from 'features/category/categorySlice';
import { sortBy } from 'GeminiViewerComponent/helpers/lodashUtils';
import useCategoryAsseyStyles from '../CardView/CardContainer/styles';
import { CategoryAccordionTable } from './components/CategoryAccordionTable';

// CategoryPanel represents "no category" selections as { category_id: 0 } with no display_name.
const NONE_CATEGORY = { category_id: 0, display_name: 'NONE' };

export default function GridView({
    assets,
    pageInfo,
    searchString,
    sortDirection,
    sortBy: tableSortBy,
    handleSort,
    loadingStatus,
    assetType,
    assetPayload,
    readOnly,
    categoryView,
    selectedAssetCategory,
    setSelectedAssetCategory,
}) {
    //#region Constants
    const classes = useCategoryAsseyStyles();
    //#endregion Constants

    //#region State
    const [expanded, setExpanded] = useState([]);
    //#endregion State

    //#region Selectors
    const allCategories = useSelector(getAllCategories);
    //#endregion Selectors

    //#region Effects
    useEffect(() => {
        setExpanded([]);
    }, [assetType]);
    //#endregion Effects

    //#region Methods
    const handleOpenCategory = (categoryId) => (event, isExpanded) => {
        setExpanded((prev) => (isExpanded ? [...prev, categoryId] : prev.filter((id) => id !== categoryId)));
    };
    //#endregion Methods

    //#region Render time calcs
    const hasSelection = selectedAssetCategory.length > 0;
    // A selected "NONE" filter is rendered as its own trailing group, same as when browsing
    // all categories, rather than mixed into the display_name sort of real categories.
    const realCategories = hasSelection
        ? selectedAssetCategory.filter((cat) => cat.category_id !== 0).sort(sortBy('display_name', 'DESC'))
        : allCategories;
    const includeNoneGroup = hasSelection ? selectedAssetCategory.some((cat) => cat.category_id === 0) : true;
    const categoriesToRender = includeNoneGroup ? [...realCategories, NONE_CATEGORY] : realCategories;
    //#endregion Render time calcs

    //#region Render
    return categoryView && allCategories && allCategories?.length > 0 ? (
        <Box display="flex" gap="10px" height="calc(100vh - 237px)" marginTop="10px">
            <CategoryPanel
                selectedAssetCategory={selectedAssetCategory}
                setSelectedAssetCategory={setSelectedAssetCategory}
                cardView={false}
                displayNoneCategory
            />
            <Box className={classes.categoryAssetWrapper}>
                <Each
                    of={categoriesToRender}
                    render={(category) => (
                        <CategoryAccordionTable
                            key={category.category_id}
                            category={category}
                            expanded={expanded.includes(category.category_id)}
                            onToggle={handleOpenCategory(category.category_id)}
                            assets={assets}
                            pageInfo={pageInfo}
                            searchString={searchString}
                            sortDirection={sortDirection}
                            sortBy={tableSortBy}
                            handleSort={handleSort}
                            loadingStatus={loadingStatus}
                            assetType={assetType}
                            assetPayload={assetPayload}
                            readOnly={readOnly}
                        />
                    )}
                />
            </Box>
        </Box>
    ) : (
        <TableGrid
            assets={assets}
            pageInfo={pageInfo}
            searchString={searchString}
            sortDirection={sortDirection}
            sortBy={tableSortBy}
            handleSort={handleSort}
            loadingStatus={loadingStatus}
            assetType={assetType}
            assetPayload={assetPayload}
            readOnly={readOnly}
        />
    );
    //#endregion Render
}
